import { useEffect, useState } from "react";
import { fetchGigs } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const monthShort = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];

function formatDate(iso) {
  const d = new Date(iso);
  return {
    day: String(d.getUTCDate()).padStart(2, "0"),
    month: monthShort[d.getUTCMonth()],
    year: String(d.getUTCFullYear()).slice(-2),
  };
}

export default function Gigs() {
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchGigs();
        setGigs(data);
      } catch (e) {
        setError("Could not load gigs.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section
      id="gigs"
      data-testid="gigs-section"
      className="relative py-24 sm:py-32 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#FF2A2A]">
              [ 02 / Tour ]
            </span>
            <h2 className="mt-4 font-heading font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">
              Upcoming
              <br />
              Transmissions
            </h2>
          </div>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#8A8A8A] max-w-xs">
            ↳ All times local. Bookings via the link at the bottom of this page.
          </p>
        </Reveal>

        {loading && (
          <p data-testid="gigs-loading" className="font-mono text-xs uppercase tracking-[0.2em] text-[#8A8A8A]">
            Loading tour dates…
          </p>
        )}
        {error && (
          <p data-testid="gigs-error" className="font-mono text-xs uppercase tracking-[0.2em] text-[#FF2A2A]">
            {error}
          </p>
        )}

        {!loading && !error && (
          <ul data-testid="gigs-list" className="border-t border-white/10">
            {gigs.map((g, idx) => {
              const d = formatDate(g.date);
              return (
                <motion.li
                  key={g.id}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.05 }}
                  transition={{ duration: 0.7, delay: idx * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`gig-row-${g.id}`}
                  className="group grid grid-cols-12 gap-3 sm:gap-6 py-6 sm:py-7 border-b border-white/10 hover:border-[#FF2A2A] transition-colors"
                >
                  {/* Date */}
                  <div className="col-span-3 sm:col-span-2 flex items-baseline gap-2 font-mono">
                    <span className="text-2xl sm:text-3xl font-bold text-[#FF2A2A]">{d.day}</span>
                    <span className="text-xs tracking-[0.2em] uppercase text-[#8A8A8A]">
                      {d.month} '{d.year}
                    </span>
                  </div>

                  {/* Venue */}
                  <div className="col-span-9 sm:col-span-5">
                    <h3 className="font-heading font-bold uppercase tracking-tight text-xl sm:text-2xl group-hover:text-white transition-colors">
                      {g.venue}
                    </h3>
                    <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#8A8A8A] mt-1">
                      {g.city} · {g.country}
                    </p>
                  </div>

                  {/* Status */}
                  <div className="col-span-12 sm:col-span-2 flex items-center font-mono text-[10px] sm:text-xs tracking-[0.25em] uppercase">
                    {g.sold_out ? (
                      <span className="text-[#FF2A2A]" data-testid={`gig-status-${g.id}`}>● Sold out</span>
                    ) : (
                      <span className="text-[#F5F5F5]/70" data-testid={`gig-status-${g.id}`}>● On sale</span>
                    )}
                  </div>

                  {/* Action */}
                  <div className="col-span-12 sm:col-span-3 flex sm:justify-end items-center">
                    {g.sold_out ? (
                      <span
                        data-testid={`gig-action-${g.id}`}
                        className="font-mono text-xs tracking-[0.25em] uppercase text-[#8A8A8A] border border-white/15 px-5 py-3 inline-flex items-center gap-2"
                      >
                        Waitlist
                      </span>
                    ) : (
                      <a
                        data-testid={`gig-tickets-${g.id}`}
                        href={g.ticket_url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-xs tracking-[0.25em] uppercase border border-[#FF2A2A] text-[#FF2A2A] px-5 py-3 inline-flex items-center gap-2 hover:bg-[#FF2A2A] hover:text-black transition-all"
                      >
                        Tickets
                        <ArrowUpRight size={14} />
                      </a>
                    )}
                  </div>
                </motion.li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
